import React from "react";
import { Moment } from "moment";
import { UltraFilter } from "~/components/ultra-filter";
import { usePayrollAdminProvider } from "~/filterProviders/employeeProviders";
import { ActiveFilter, FilterProvider } from "~/components/ultra-filter/types";
import { useWeekStatusProvider } from "~/filterProviders/useWeekStatusProvider";
import { useWorkLocationProvider } from "~/filterProviders/useWorkLocationProvider";
import { useDateProvider } from "~/filterProviders/useDateProvider";
import { useReimbursementProvider } from "~/visuals/pages/TimesheetReview/useReimbursementProvider";
import { usePayPeriodProvider } from "~/visuals/pages/TimesheetReview/usePayPeriodProvider";
import {
  FilterFormProps,
  ShowFutureFilter,
} from "~/visuals/organisms/TimesheetDisplay/types";
import {
  Employee,
  PayPeriod,
  TimesheetSearchReimbursementOption,
  TimesheetStatus,
} from "~/gql/types";
import {
  useCertifiedPayrollProvider,
  useShowFutureTimesheetsProvider,
} from "~/filterProviders/yesNoProviders";

export type FilterOptions = {
  searchText: string | null;
  weekStart: Moment | null;
  dateBefore: Moment | null;
  dateAfter: Moment | null;
  workLocation: string | null;
  weekStatus: TimesheetStatus | null;
  reimbursement: TimesheetSearchReimbursementOption | null;
  payrollAdmin: Employee | null;
  payPeriod: PayPeriod | null;
  showFuture: ShowFutureFilter;
  certifiedPayroll: ShowFutureFilter;
};

export const FilterForm: React.FC<FilterFormProps> = ({
  onFiltersChanged,
  dialogTitle,
  label,
  type,
}) => {
  const payrollAdminFilter = usePayrollAdminProvider({
    documentationText: "The employee's payroll admin.",
  });
  const payPeriodFilter = usePayPeriodProvider({
    documentationText: "The employee's pay period.",
  });
  const officeFilter = useWorkLocationProvider({
    documentationText: "The employee's work location.",
  });

  const providers = [
    useDateProvider({
      type: "weekStart",
      label: "Timesheet Week",
      documentationText: "The date of the timesheet week",
      suggestText: "date",
    }),
    useDateProvider({
      type: "dateBefore",
      label: "Date Before",
      documentationText:
        "Search for timesheets beginning before this date (exclusive).",
      suggestText: "dbf",
    }),
    useDateProvider({
      type: "dateAfter",
      label: "Date After",
      documentationText:
        "Search for timesheets beginning after this date (exclusive).",
      suggestText: "daft",
    }),
    useWeekStatusProvider({ documentationText: "The timesheet status." }),
    useReimbursementProvider({
      documentationText: "The reimbursement type for the timesheet's expenses.",
    }),
    useShowFutureTimesheetsProvider({
      documentationText: "Show next week's timesheet",
    }),
    useCertifiedPayrollProvider({
      documentationText: "Show timesheets with certified payroll project time",
    }),
  ] as FilterProvider[];

  if (type != "MyExpenses") {
    providers.push(payrollAdminFilter, payPeriodFilter, officeFilter);
  }

  const onChanged = (filters: ActiveFilter[], searchText: string) => {
    searchText;

    const newFilters = {
      searchText: searchText || null,
      weekStart: filters.find((f) => f.type === "weekStart")?.value || null,
      dateBefore: filters.find((f) => f.type === "dateBefore")?.value || null,
      dateAfter: filters.find((f) => f.type === "dateAfter")?.value || null,
      weekStatus: filters.find((f) => f.type === "weekStatus")?.value || null,
      workLocation:
        filters.find((f) => f.type === "workLocation")?.value || null,
      reimbursement:
        filters.find((f) => f.type === "reimbursement")?.value || null,
      payrollAdmin:
        filters.find((f) => f.type === "payrollAdmin")?.value || null,
      payPeriod: filters.find((f) => f.type === "payPeriod")?.value || null,
      showFuture: filters.find((f) => f.type === "showFuture")?.value || null,
      certifiedPayroll:
        filters.find((f) => f.type === "certifiedPayroll")?.value || null,
    };

    onFiltersChanged(newFilters);
  };

  return (
    <div className="filter-form">
      <UltraFilter
        {...{
          providers,
          dialogTitle,
          label,
          onFiltersChanged: onChanged,
        }}
      />
    </div>
  );
};
